import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
    useMemo,
    memo,
    forwardRef,
    useImperativeHandle,
} from 'react';
import isEqual from 'lodash/isEqual';
import { Container, Stack, Typography, Box, Paper, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IProject, IWorkProfile, ISound } from '../../types/global/helper';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { CastingDetails } from './CastingDetails';
import { PhotoGrid } from './PhotoGrid';
import { DemoReelGrid } from './DemoReelGrid';
import { AudioReelGrid } from './AudioReelGrid';
import { TMediaType, UserProjectSearchDialogue } from '../UserProjectSearchDialogue/UserProjectSearchDialogue';
import { generateShareLink, getIdForIbjects } from '../../utils/AppUtils';
import { PhotoUploadedWithCropAndCompress } from '../EsForm/ProjectThumbnailUpload';
import { theme } from '../../constants/theme';
import { IApplication } from '../../types/global/ListRender.types';
import { VideoThumbnail } from '../VideoPlayer/VideoThumbnail';
import { ThemeStyle } from '../../module/ThemeManager';
import { batch, useDispatch } from 'react-redux';
import { setmedia } from '../../redux/project/actions';
import { setMute } from '../../redux/player/action';
import { setVideoViewCount } from '../../redux/adverts/action';
import { useAuth } from '../../module/AuthContext';
import { LinkBox } from '../LinkBox';
import Compressor from 'compressorjs';
import { blobToDataURL } from '../ProfileHeaderUpload/helpers';
import { v4 as uuidv4 } from 'uuid';
export type TUpdateBody = {
    body: any;
    uploads: any;
};

/** Exposed so the parent hero "Save" can PATCH demo/audio reels, gallery photos, and profile picture uploads. */
export type CastingProfileSaveHandle = {
    getMediaSavePayload: () => TUpdateBody;
};
export interface IWorkProfileProps {
    workProfile?: IWorkProfile;
    isEditMode?: boolean;
    /** Fires when hero Save should reflect unsaved media (gallery / reels / picture vs server). */
    onHeroUnsavedMediaChange?: (hasUnsavedMedia: boolean) => void;
    onChange?: (payload: TUpdateBody) => void;
    isPreviewMode?: boolean;
    applicationData?: IApplication;
    onAcceptApplication?: () => void;
    onRejectApplication?: () => void;
    onMessageApplication?: () => void;
}

// Component to display application media and notes
const ApplicationMediaAndNotes: React.FC<{
    attached_media?: IProject[];
    note?: string;
    themeStyle: ThemeStyle;
    onClickPlay?: (video: IProject) => void;
}> = memo(({ attached_media, note, themeStyle, onClickPlay }) => {
    // Use useRef to track already played videos to avoid multiple view count updates
    const playedVideosRef = useRef<Set<string>>(new Set());

    const handleVideoClick = useCallback(
        (video: IProject) => {
            if (onClickPlay) {
                // Check if this video was already played
                const videoId = video._id || '';
                if (!playedVideosRef.current.has(videoId)) {
                    playedVideosRef.current.add(videoId);
                    onClickPlay(video);
                } else {
                    // For already played videos, just call onClickPlay without view count update
                    // We'll use the videoId to determine whether to update view count
                    onClickPlay(video);
                }
            }
        },
        [onClickPlay],
    );

    return (
        <Stack spacing={2} mb={3}>
            {attached_media && (
                <Box>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '18px',
                            mb: 1,
                        }}>
                        Applicant Video
                    </Typography>
                    <Stack>
                        {attached_media?.map((video, index) => (
                            <VideoThumbnail
                                key={`video-${video._id || index}`}
                                project={video}
                                onClick={() => handleVideoClick(video)}
                            />
                        ))}
                    </Stack>
                </Box>
            )}

            {note && (
                <Box>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '18px',
                            mb: 1,
                        }}>
                        Applicant Note
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            backgroundColor: theme[themeStyle as keyof typeof theme].form_black,
                            borderRadius: '8px',
                        }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '14px',
                                whiteSpace: 'pre-wrap',
                            }}>
                            {note}
                        </Typography>
                    </Paper>
                </Box>
            )}
        </Stack>
    );
});

// const EXEMPTED = ['profile_type', 'photos', 'profile_picture', 'demo_reel', 'audio_reel'];
const CastingProfileInner = forwardRef<CastingProfileSaveHandle, IWorkProfileProps>(function CastingProfileInner(
    {
        workProfile,
        isEditMode,
        onHeroUnsavedMediaChange,
        onChange,
        isPreviewMode,
        applicationData,
        onAcceptApplication,
        onRejectApplication,
        onMessageApplication,
    },
    ref,
) {
        const { themeStyle, device } = useThemeContext();
        const muiTheme = useTheme();
        /** Matches `Grid` lg split: stacked columns below lg need reels + share after both detail columns. */
        const isBelowLg = useMediaQuery(muiTheme.breakpoints.down('lg'));
        /** Default performer layout: below md, one column with reels after details (was two-column from `sm`). */
        const isBelowMd = useMediaQuery(muiTheme.breakpoints.down('md'));
        const [form, setForm] = useState<IWorkProfile>(workProfile!);
        const [uploads, setUploads] = useState<any>({});
        const [uploadField, setUploadField] = useState('');
        const fileInputRef = useRef<HTMLInputElement>(null);
        const [openSearch, setOpenSearch] = useState<boolean>(false);
        const [mediaType, setMediaType] = useState<TMediaType>();
        const [, setOpenDetailsForm] = useState(false);
        const viewCountUpdatedVideos = useRef<Set<string>>(new Set());
        const { isSignedIn, user } = useAuth();
        const dispatch = useDispatch();

        // --- MEDIA FORM STATE ---
        const [mediaForm, setMediaForm] = useState({
            profile_picture: workProfile?.profile_picture || '',
            photos: workProfile?.photos || [],
            demo_reel: workProfile?.demo_reel || [],
            audio_reel: workProfile?.audio_reel || [],
        });

        // Sync mediaForm with workProfile changes
        useEffect(() => {
            if (workProfile) {
                setMediaForm({
                    profile_picture: workProfile.profile_picture || '',
                    photos: workProfile.photos || [],
                    demo_reel: workProfile.demo_reel || [],
                    audio_reel: workProfile.audio_reel || [],
                });
                setForm(workProfile);
                setUploads({});
            }
        }, [workProfile, isEditMode]);

        const hasPendingUploads = useMemo(
            () =>
                Boolean(
                    (Array.isArray(uploads.photos) && uploads.photos.length > 0) || uploads.profile_picture,
                ),
            [uploads.photos, uploads.profile_picture],
        );

        const hasUnsavedHeroMedia = useMemo(() => {
            if (!isEditMode || !workProfile) return false;
            if (hasPendingUploads) return true;
            if (!isEqual(mediaForm.photos || [], workProfile.photos || [])) return true;
            const demoIds = (mediaForm.demo_reel || []).map((p: IProject) => String(p._id || ''));
            const wDemoIds = (workProfile.demo_reel || []).map((p: IProject) => String(p._id || ''));
            if (!isEqual(demoIds, wDemoIds)) return true;
            const audioIds = (mediaForm.audio_reel || []).map((s: ISound) => String(s._id || ''));
            const wAudioIds = (workProfile.audio_reel || []).map((s: ISound) => String(s._id || ''));
            if (!isEqual(audioIds, wAudioIds)) return true;
            const ppForm = typeof mediaForm.profile_picture === 'string' ? mediaForm.profile_picture : '';
            const ppWp = typeof workProfile.profile_picture === 'string' ? workProfile.profile_picture : '';
            return ppForm !== ppWp;
        }, [isEditMode, workProfile, mediaForm, hasPendingUploads]);

        useEffect(() => {
            onHeroUnsavedMediaChange?.(hasUnsavedHeroMedia);
        }, [hasUnsavedHeroMedia, onHeroUnsavedMediaChange]);

        useImperativeHandle(
            ref,
            () => ({
                getMediaSavePayload: (): TUpdateBody => {
                    if (!workProfile) {
                        return { body: {}, uploads: undefined };
                    }
                    const uploadsPayload: Record<string, unknown> = {};
                    if (Array.isArray(uploads.photos) && uploads.photos.length > 0) {
                        uploadsPayload.photos = uploads.photos;
                    }
                    if (uploads.profile_picture) {
                        const pp = uploads.profile_picture;
                        uploadsPayload.profile_picture = Array.isArray(pp) ? pp : [pp];
                    }
                    const demo_reel = (mediaForm.demo_reel || []).map((p: IProject) => p._id).filter(Boolean);
                    const audio_reel = (mediaForm.audio_reel || []).map((s: ISound) => s._id).filter(Boolean);
                    const body: Record<string, unknown> = {
                        photos: [...(mediaForm.photos || [])],
                        demo_reel,
                        audio_reel,
                        profile_picture: uploadsPayload.profile_picture
                            ? workProfile.profile_picture
                            : typeof mediaForm.profile_picture === 'string'
                              ? mediaForm.profile_picture
                              : workProfile.profile_picture,
                    };
                    return {
                        body,
                        uploads: Object.keys(uploadsPayload).length > 0 ? uploadsPayload : undefined,
                    };
                },
            }),
            [mediaForm, uploads, workProfile],
        );

        // --- MEDIA CHANGE HANDLERS ---
        const handleMediaChange = (field: keyof typeof mediaForm, value: any) => {
            setMediaForm(prev => {
                const currentValue = prev[field];
                let updatedValue;

                if (Array.isArray(currentValue)) {
                    if (Array.isArray(value)) {
                        updatedValue = [...currentValue, ...value];
                    } else {
                        updatedValue = [...currentValue, value];
                    }
                } else {
                    updatedValue = value;
                }

                return {
                    ...prev,
                    [field]: updatedValue,
                };
            });
        };

        // Use useCallback to memoize the function to prevent re-creation on each render
        const bumpDemoReelViewCount = useCallback(
            (project: IProject) => {
                const videoId = project._id || '';
                if (viewCountUpdatedVideos.current.has(videoId)) {
                    return;
                }
                viewCountUpdatedVideos.current.add(videoId);
                setTimeout(() => {
                    dispatch(setVideoViewCount(1));
                }, 300);
            },
            [dispatch],
        );

        /** Opens the global vertical / act player (Details, Media expand, application media). */
        const openActPlayerForProject = useCallback(
            (project: IProject) => {
                dispatch(
                    setmedia({
                        media: project,
                        mediaType: 'act',
                    }),
                );
                batch(() => {
                    dispatch(setMute(false));
                });
                bumpDemoReelViewCount(project);
            },
            [dispatch, bumpDemoReelViewCount],
        );

        // Image compression utility function
        const compressImage = (file: File): Promise<{ compressedDataUrl: string; compressedFile: File }> => {
            return new Promise((resolve, reject) => {
                console.log(`Original file size: ${(file.size / 1024).toFixed(2)} KB`);

                new Compressor(file, {
                    quality: 0.7,
                    maxWidth: 1600,
                    maxHeight: 1600,
                    convertSize: 100 * 1024, // 100KB target size
                    success: compressedFile => {
                        console.log(`Compressed file size: ${(compressedFile.size / 1024).toFixed(2)} KB`);
                        console.log(
                            `Compression ratio: ${((1 - compressedFile.size / file.size) * 100).toFixed(1)}% reduction`,
                        );

                        blobToDataURL(compressedFile)
                            .then(compressedDataUrl => {
                                const finalFile = new File([compressedFile], uuidv4(), {
                                    type: compressedFile.type,
                                });
                                resolve({
                                    compressedDataUrl,
                                    compressedFile: finalFile,
                                });
                            })
                            .catch(reject);
                    },
                    error: reject,
                });
            });
        };

        const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files;
            if (files && files.length > 0) {
                const newFiles: File[] = Array.from(files);

                try {
                    if (uploadField === 'profile_picture') {
                        // Compress single profile picture
                        const { compressedDataUrl, compressedFile } = await compressImage(newFiles[0]);
                        handleMediaChange('profile_picture', compressedDataUrl);
                        setUploads((prevState: any) => ({
                            ...prevState,
                            profile_picture: compressedFile,
                        }));
                    } else if (uploadField === 'photos') {
                        // Compress multiple photos
                        const compressionPromises = newFiles.map(file => compressImage(file));
                        const compressedResults = await Promise.all(compressionPromises);

                        const compressedDataUrls = compressedResults.map(result => result.compressedDataUrl);
                        const compressedFiles = compressedResults.map(result => result.compressedFile);

                        handleMediaChange('photos', compressedDataUrls);
                        setUploads((prevState: any) => ({
                            ...prevState,
                            photos: [...(prevState.photos || []), ...compressedFiles],
                        }));
                    }
                } catch (error) {
                    console.error('Error compressing files: ', error);
                }
            }
        };

        const handleUpload = useCallback((field: string) => {
            if (fileInputRef.current) {
                setUploadField(field);
                fileInputRef.current.click();
            }
        }, []);

        const onSubmit = useCallback((payload: any[], field: string) => {
            setMediaForm((prevState: any) => {
                const existingItems = prevState[field] || [];
                const newItems = payload.filter(
                    item => !existingItems.some((existing: { _id: any }) => existing._id === item._id),
                );

                return {
                    ...prevState,
                    [field]: [...existingItems, ...newItems],
                };
            });
        }, []);

        const onEditCLick = useCallback(() => {
            setOpenDetailsForm(prev => !prev);
        }, []);

        const isTechnicalProfile = form.profile_type === 'technical';
        const isContentCreatorProfile = form.profile_type === 'content_creator';
        const isArtistProfile = form.profile_type === 'artist';
        const isPerformerProfile = form.profile_type === 'performer';
        const isVoiceoverProfile = form.profile_type === 'voiceover';
        const pageCanvasBg = theme[themeStyle].form_gray;

        const searchDialogueAndInput = (
            <>
                <UserProjectSearchDialogue
                    open={openSearch}
                    type={mediaType}
                    enableAdd={mediaType === 'audio'}
                    onClose={() => setOpenSearch(!openSearch)}
                    onSubmit={payload => {
                        onSubmit(payload, mediaType === 'audio' ? 'audio_reel' : 'demo_reel');
                    }}
                />
                <input
                    type="file"
                    accept="image/*"
                    multiple={uploadField === 'photos'}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
            </>
        );

        const profilePictureBlock = (
            <>
                        {!isEditMode && (
                            <CustomLazyLoadImage
                                src={mediaForm.profile_picture}
                                withPreview
                                isEditMode={isEditMode}
                                onEditClick={() => handleUpload('profile_picture')}
                                style={{
                                    height: 'auto',
                                    maxWidth: device.isMobile ? '100%' : '300px',
                                    objectFit: 'cover',
                            borderRadius: isTechnicalProfile ? 16 : 5,
                                }}
                            />
                        )}
                        {isEditMode && (
                            <PhotoUploadedWithCropAndCompress
                                showDisplay
                                customAspectRation={0.7}
                                style={{
                                    height: '450px',
                                    maxWidth: device.isMobile ? '100%' : '300px',
                                    objectFit: 'cover',
                            borderRadius: isTechnicalProfile ? 16 : 5,
                                }}
                                initialValue={mediaForm.profile_picture}
                                aspectRatio="square"
                                setPreview={function (url: string, file: File): void {
                                    handleMediaChange('profile_picture', url);
                                    setUploads((prevState: any) => ({
                                        ...prevState,
                                        profile_picture: [file],
                                    }));
                                }}
                            />
                        )}
            </>
        );

        const imagesBlock = (
            <>
                <Typography
                    sx={{
                        mt: 2,
                        mb: 0.5,
                        px: isTechnicalProfile ? 0 : 1,
                        color: theme[themeStyle].highlight,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        ...(isTechnicalProfile
                            ? {
                                  fontSize: 10,
                                  fontWeight: 700,
                                  letterSpacing: '0.14em',
                                  textTransform: 'uppercase',
                              }
                            : { fontSize: 18 }),
                    }}>
                    Images
                </Typography>
                        <PhotoGrid
                            photos={mediaForm.photos}
                            isEditMode={isEditMode}
                            onAddPhoto={() => handleUpload('photos')}
                            onEditClick={index => {
                                setMediaForm(prevState => ({
                                    ...prevState,
                                    photos: mediaForm.photos.filter((_, i) => i !== index),
                                }));
                            }}
                        />
            </>
        );

        const reelEmptyVisitorView = !isSignedIn || getIdForIbjects(form.user) !== user?._id;

        const demoReelBlock = (
                        <DemoReelGrid
                            projects={mediaForm.demo_reel}
                            themeStyle={themeStyle}
                            isEditMode={isEditMode}
                            onExpandToPlayer={openActPlayerForProject}
                            onInlinePlaybackStart={bumpDemoReelViewCount}
                            onDelete={project => {
                                setMediaForm(prevState => ({
                                    ...prevState,
                                    demo_reel: mediaForm.demo_reel.filter(proj => proj._id !== project._id),
                                }));
                            }}
                            onAddNewItem={() => {
                                setMediaType('video');
                                setOpenSearch(!openSearch);
                            }}
                            title={
                                isArtistProfile || isPerformerProfile ? 'Featured Demo Reel' : undefined
                            }
                            artistStudioTitle={isArtistProfile || isPerformerProfile}
                            compactSectionTitle={isTechnicalProfile && !isArtistProfile && !isPerformerProfile}
                            alignWithTitleCard={isContentCreatorProfile || isVoiceoverProfile}
                            flushLayout={isVoiceoverProfile || isContentCreatorProfile}
                            visitorView={reelEmptyVisitorView}
                        />
        );

        const audioReelBlock =
            workProfile?.profile_type !== 'content_creator' ? (
                            <AudioReelGrid
                                sounds={mediaForm.audio_reel}
                                themeStyle={themeStyle}
                                isEditMode={isEditMode}
                                flushLayout={(isTechnicalProfile && !isArtistProfile && !isPerformerProfile) || isVoiceoverProfile}
                                alignWithTitleCard={isVoiceoverProfile}
                                onDelete={audio => {
                                    setMediaForm(prevState => ({
                                        ...prevState,
                                        audio_reel: (mediaForm.audio_reel || []).filter(s => s._id !== audio._id),
                                    }));
                                }}
                                onAddNewItem={() => {
                                    setMediaType('audio');
                                    setOpenSearch(!openSearch);
                                }}
                    compactSectionTitle={isTechnicalProfile && !isArtistProfile && !isPerformerProfile}
                    artistStudioCard={isArtistProfile || isPerformerProfile}
                    visitorView={reelEmptyVisitorView}
                />
            ) : null;

        const shareLinkBlock = workProfile ? (
            <LinkBox
                link={generateShareLink('profile', workProfile, true)}
                {...(isTechnicalProfile || isArtistProfile ? { variant: 'technical' as const } : {})}
            />
        ) : null;


        const previewModeValue = !isSignedIn || (isSignedIn && getIdForIbjects(form.user) !== user?._id);

        const technicalLeftDetails = (
            <Stack spacing={2.5}>
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-tech-left-main"
                    technicalLayout
                    allowedSections={['title', 'about']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
                <Box
                    sx={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: 2.5,
                        alignItems: 'stretch',
                        justifyItems: 'stretch',
                    }}>
                    <Box sx={{ width: '100%', minWidth: 0, display: 'flex', height: '100%' }}>
                        <CastingDetails
                            profile={form}
                            themeStyle={themeStyle}
                            isEditMode={isEditMode}
                            onEditClick={onEditCLick}
                            previewMode={previewModeValue}
                            editFocusScope="wp-tech-left-skills"
                            technicalLayout
                            allowedSections={['skills']}
                            onSubmitForm={payload =>
                                onChange &&
                                onChange({
                                    body: payload,
                                    uploads: undefined,
                                })
                            }
                        />
                    </Box>
                    <Box sx={{ width: '100%', minWidth: 0, display: 'flex', height: '100%' }}>
                        <CastingDetails
                            profile={form}
                            themeStyle={themeStyle}
                            isEditMode={isEditMode}
                            onEditClick={onEditCLick}
                            previewMode={previewModeValue}
                            editFocusScope="wp-tech-left-other"
                            technicalLayout
                            allowedSections={['other_skills']}
                            onSubmitForm={payload =>
                                onChange &&
                                onChange({
                                    body: payload,
                                    uploads: undefined,
                                })
                            }
                        />
                    </Box>
                </Box>
                    </Stack>
        );

        const technicalRightDetails = (
            <Stack spacing={2.5}>
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-tech-right-loc"
                    technicalLayout
                    allowedSections={['location']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-tech-right-misc"
                    technicalLayout
                    singleCardSections
                    allowedSections={['language_spoken', 'special_skills', 'links']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
                    </Stack>
        );

        const contentCreatorTitleSocials = (
            <Stack spacing={2.5}>
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-cc-title"
                    technicalLayout
                    allowedSections={['title']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-cc-socials"
                    technicalLayout
                    suppressDirectChildCard
                    allowedSections={['socials']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
            </Stack>
        );

        const contentCreatorLeftDetails = (
            <Stack spacing={2.5}>
                {contentCreatorTitleSocials}
                {demoReelBlock}
            </Stack>
        );

        const contentCreatorRightDetails = (
            <Stack spacing={2.5}>
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-cc-loc"
                    technicalLayout
                    allowedSections={['location']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-cc-skills"
                    technicalLayout
                    allowedSections={['skills', 'other_skills']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-cc-lang"
                    technicalLayout
                    allowedSections={['language_spoken', 'special_skills']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-cc-links"
                    technicalLayout
                    allowedSections={['links']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
            </Stack>
        );

        const detailsStack = (
            <Stack sx={{ flex: 1, width: '100%' }}>
                        {isPreviewMode && onAcceptApplication && onRejectApplication && onMessageApplication && (
                            <Box mb={3}>{/* PreviewModeActions would be here if we implemented it */}</Box>
                        )}
                        {applicationData && (
                            <ApplicationMediaAndNotes
                                themeStyle={themeStyle}
                                attached_media={applicationData.attached_media}
                                note={applicationData.note}
                                onClickPlay={openActPlayerForProject}
                            />
                        )}
                        <CastingDetails
                            profile={form}
                            themeStyle={themeStyle}
                            isEditMode={isEditMode}
                            onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-legacy-details"
                    technicalLayout={isTechnicalProfile}
                            onSubmitForm={payload =>
                                onChange &&
                                onChange({
                                    body: payload,
                                    uploads: undefined,
                                })
                            }
                        />
                    </Stack>
        );

        const voiceoverLeftDetails = (
            <Stack spacing={2.5}>
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-voiceover-main"
                    technicalLayout
                    allowedSections={['title', 'about']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
                <Box
                    sx={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: 2.5,
                        alignItems: 'stretch',
                        justifyItems: 'stretch',
                    }}>
                    <Box sx={{ width: '100%', minWidth: 0, display: 'flex', height: '100%' }}>
                        <CastingDetails
                            profile={form}
                            themeStyle={themeStyle}
                            isEditMode={isEditMode}
                            onEditClick={onEditCLick}
                            previewMode={previewModeValue}
                            editFocusScope="wp-voiceover-skills"
                            technicalLayout
                            allowedSections={['skills']}
                            onSubmitForm={payload =>
                                onChange &&
                                onChange({
                                    body: payload,
                                    uploads: undefined,
                                })
                            }
                        />
                    </Box>
                    <Box sx={{ width: '100%', minWidth: 0, display: 'flex', height: '100%' }}>
                        <CastingDetails
                            profile={form}
                            themeStyle={themeStyle}
                            isEditMode={isEditMode}
                            onEditClick={onEditCLick}
                            previewMode={previewModeValue}
                            editFocusScope="wp-voiceover-other"
                            technicalLayout
                            allowedSections={['other_skills']}
                            onSubmitForm={payload =>
                                onChange &&
                                onChange({
                                    body: payload,
                                    uploads: undefined,
                                })
                            }
                        />
                    </Box>
                </Box>
            </Stack>
        );

        const voiceoverRightDetails = (
            <Stack spacing={2.5}>
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-voiceover-identity"
                    technicalLayout
                    singleCardSections
                    artistPlainLocation
                    allowedSections={['location', 'attributes']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-voiceover-meta"
                    technicalLayout
                    singleCardSections
                    allowedSections={['language_spoken', 'ethnicity', 'special_skills']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
                <CastingDetails
                    profile={form}
                    themeStyle={themeStyle}
                    isEditMode={isEditMode}
                    onEditClick={onEditCLick}
                    previewMode={previewModeValue}
                    editFocusScope="wp-voiceover-links"
                    technicalLayout
                    allowedSections={['links']}
                    onSubmitForm={payload =>
                        onChange &&
                        onChange({
                            body: payload,
                            uploads: undefined,
                        })
                    }
                />
            </Stack>
        );

        return (
            <Container
                maxWidth={false}
                sx={{
                    maxWidth: 'min(1280px, 100%)',
                    mx: 'auto',
                    px: { xs: 2, sm: 3 },
                    /* Same vertical inset as technical for all profile types (nav → content gap). */
                    py: 2,
                    ...(isTechnicalProfile || isArtistProfile || isPerformerProfile
                        ? {
                              bgcolor: pageCanvasBg,
                              borderRadius: 0,
                          }
                        : {}),
                }}>
                {searchDialogueAndInput}
                {isTechnicalProfile ? (
                    isBelowLg ? (
                        <Stack spacing={4}>
                            <Stack spacing={2.5}>
                                {technicalLeftDetails}
                                {technicalRightDetails}
                </Stack>
                            {demoReelBlock}
                            {audioReelBlock}
                            {shareLinkBlock}
                        </Stack>
                    ) : (
                        <Grid container spacing={4}>
                            <Grid item xs={12} lg={8}>
                                <Stack spacing={2.5}>
                                    {technicalLeftDetails}
                                    {demoReelBlock}
                                </Stack>
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <Stack spacing={2.5}>
                                    {technicalRightDetails}
                                    {audioReelBlock}
                                    {shareLinkBlock}
                                </Stack>
                            </Grid>
                        </Grid>
                    )
                ) : isContentCreatorProfile ? (
                    isBelowLg ? (
                        <Stack spacing={4}>
                            <Stack spacing={2.5}>
                                {contentCreatorTitleSocials}
                                {contentCreatorRightDetails}
                            </Stack>
                            {demoReelBlock}
                            {audioReelBlock}
                            {shareLinkBlock}
                        </Stack>
                    ) : (
                        <Grid container spacing={4}>
                            <Grid item xs={12} lg={8}>
                                {contentCreatorLeftDetails}
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                {contentCreatorRightDetails}
                            </Grid>
                        </Grid>
                    )
                ) : isArtistProfile ? (
                    isBelowLg ? (
                        <Stack spacing={4}>
                            <Stack spacing={3}>
                                {demoReelBlock}
                                {audioReelBlock}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                        gap: 2,
                                        alignItems: 'stretch',
                                    }}>
                                    <CastingDetails
                                        profile={form}
                                        themeStyle={themeStyle}
                                        isEditMode={isEditMode}
                                        onEditClick={onEditCLick}
                                        previewMode={previewModeValue}
                                        editFocusScope="wp-artist-skills-a"
                                        technicalLayout
                                        allowedSections={['skills']}
                                        onSubmitForm={payload =>
                                            onChange &&
                                            onChange({
                                                body: payload,
                                                uploads: undefined,
                                            })
                                        }
                                    />
                                    <CastingDetails
                                        profile={form}
                                        themeStyle={themeStyle}
                                        isEditMode={isEditMode}
                                        onEditClick={onEditCLick}
                                        previewMode={previewModeValue}
                                        editFocusScope="wp-artist-skills-b"
                                        technicalLayout
                                        allowedSections={['other_skills']}
                                        onSubmitForm={payload =>
                                            onChange &&
                                            onChange({
                                                body: payload,
                                                uploads: undefined,
                                            })
                                        }
                                    />
                                </Box>
                                <CastingDetails
                                    profile={form}
                                    themeStyle={themeStyle}
                                    isEditMode={isEditMode}
                                    onEditClick={onEditCLick}
                                    previewMode={previewModeValue}
                                    editFocusScope="wp-artist-about"
                                    technicalLayout
                                    artistStudioAboutHeader
                                    artistMergedAboutMeta
                                    allowedSections={[
                                        'about',
                                        'language_spoken',
                                        'ethnicity',
                                        'special_skills',
                                    ]}
                                    onSubmitForm={payload =>
                                        onChange &&
                                        onChange({
                                            body: payload,
                                            uploads: undefined,
                                        })
                                    }
                                />
                            </Stack>
                            <CastingDetails
                                profile={form}
                                themeStyle={themeStyle}
                                isEditMode={isEditMode}
                                onEditClick={onEditCLick}
                                previewMode={previewModeValue}
                                editFocusScope="wp-artist-sidebar"
                                technicalLayout
                                singleCardSections
                                artistPlainLocation
                                allowedSections={['stage_name', 'age_range', 'location', 'attributes']}
                                onSubmitForm={payload =>
                                    onChange &&
                                    onChange({
                                        body: payload,
                                        uploads: undefined,
                                    })
                                }
                            />
                            <CastingDetails
                                profile={form}
                                themeStyle={themeStyle}
                                isEditMode={isEditMode}
                                onEditClick={onEditCLick}
                                previewMode={previewModeValue}
                                editFocusScope="wp-artist-links"
                                technicalLayout
                                allowedSections={['links']}
                                onSubmitForm={payload =>
                                    onChange &&
                                    onChange({
                                        body: payload,
                                        uploads: undefined,
                                    })
                                }
                            />
                            {shareLinkBlock}
                        </Stack>
                    ) : (
                        <Grid container spacing={4}>
                            <Grid item xs={12} lg={8}>
                                <Stack spacing={3}>
                                    {demoReelBlock}
                                    {audioReelBlock}
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                            gap: 2,
                                            alignItems: 'stretch',
                                        }}>
                                        <CastingDetails
                                            profile={form}
                                            themeStyle={themeStyle}
                                            isEditMode={isEditMode}
                                            onEditClick={onEditCLick}
                                            previewMode={previewModeValue}
                                            editFocusScope="wp-artist-lg-skills-a"
                                            technicalLayout
                                            allowedSections={['skills']}
                                            onSubmitForm={payload =>
                                                onChange &&
                                                onChange({
                                                    body: payload,
                                                    uploads: undefined,
                                                })
                                            }
                                        />
                                        <CastingDetails
                                            profile={form}
                                            themeStyle={themeStyle}
                                            isEditMode={isEditMode}
                                            onEditClick={onEditCLick}
                                            previewMode={previewModeValue}
                                            editFocusScope="wp-artist-lg-skills-b"
                                            technicalLayout
                                            allowedSections={['other_skills']}
                                            onSubmitForm={payload =>
                                                onChange &&
                                                onChange({
                                                    body: payload,
                                                    uploads: undefined,
                                                })
                                            }
                                        />
                                    </Box>
                                    <CastingDetails
                                        profile={form}
                                        themeStyle={themeStyle}
                                        isEditMode={isEditMode}
                                        onEditClick={onEditCLick}
                                        previewMode={previewModeValue}
                                        editFocusScope="wp-artist-lg-about"
                                        technicalLayout
                                        artistStudioAboutHeader
                                        artistMergedAboutMeta
                                        allowedSections={[
                                            'about',
                                            'language_spoken',
                                            'ethnicity',
                                            'special_skills',
                                        ]}
                                        onSubmitForm={payload =>
                                            onChange &&
                                            onChange({
                                                body: payload,
                                                uploads: undefined,
                                            })
                                        }
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <Stack spacing={3}>
                                    <CastingDetails
                                        profile={form}
                                        themeStyle={themeStyle}
                                        isEditMode={isEditMode}
                                        onEditClick={onEditCLick}
                                        previewMode={previewModeValue}
                                        editFocusScope="wp-artist-lg-sidebar"
                                        technicalLayout
                                        singleCardSections
                                        artistPlainLocation
                                        allowedSections={[
                                            'stage_name',
                                            'age_range',
                                            'location',
                                            'attributes',
                                        ]}
                                        onSubmitForm={payload =>
                                            onChange &&
                                            onChange({
                                                body: payload,
                                                uploads: undefined,
                                            })
                                        }
                                    />
                                    <CastingDetails
                                        profile={form}
                                        themeStyle={themeStyle}
                                        isEditMode={isEditMode}
                                        onEditClick={onEditCLick}
                                        previewMode={previewModeValue}
                                        editFocusScope="wp-artist-lg-links"
                                        technicalLayout
                                        allowedSections={['links']}
                                        onSubmitForm={payload =>
                                            onChange &&
                                            onChange({
                                                body: payload,
                                                uploads: undefined,
                                            })
                                        }
                                    />
                                    {shareLinkBlock}
                                </Stack>
                            </Grid>
                        </Grid>
                    )
                ) : isPerformerProfile ? (
                    <Grid container spacing={{ xs: 4, md: 5 }} sx={{ py: { xs: 2, md: 4 } }}>
                        <Grid item xs={12} lg={8}>
                            <Stack spacing={{ xs: 3.5, md: 4.5 }}>
                                <CastingDetails
                                    profile={form}
                                    themeStyle={themeStyle}
                                    isEditMode={isEditMode}
                                    onEditClick={onEditCLick}
                                    previewMode={previewModeValue}
                                    editFocusScope="wp-perf-about"
                                    technicalLayout
                                    allowedSections={['about']}
                                    performerCinematicLayout
                                    suppressDirectChildCard
                                    onSubmitForm={payload =>
                                        onChange &&
                                        onChange({
                                            body: payload,
                                            uploads: undefined,
                                        })
                                    }
                                />
                                <CastingDetails
                                    profile={form}
                                    themeStyle={themeStyle}
                                    isEditMode={isEditMode}
                                    onEditClick={onEditCLick}
                                    previewMode={previewModeValue}
                                    editFocusScope="wp-perf-skills"
                                    technicalLayout
                                    allowedSections={['skills', 'other_skills']}
                                    performerCinematicLayout
                                    mergeExpertiseSkills={!isEditMode}
                                    suppressDirectChildCard
                                    onSubmitForm={payload =>
                                        onChange &&
                                        onChange({
                                            body: payload,
                                            uploads: undefined,
                                        })
                                    }
                                />
                                {demoReelBlock}
                                {audioReelBlock}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <Stack spacing={3}>
                                <CastingDetails
                                    profile={form}
                                    themeStyle={themeStyle}
                                    isEditMode={isEditMode}
                                    onEditClick={onEditCLick}
                                    previewMode={previewModeValue}
                                    editFocusScope="wp-perf-identity"
                                    technicalLayout
                                    singleCardSections
                                    artistPlainLocation
                                    performerSidebarEyebrow="Core Identity"
                                    performerSidebarCompact
                                    allowedSections={['stage_name', 'age_range', 'location']}
                                    onSubmitForm={payload =>
                                        onChange &&
                                        onChange({
                                            body: payload,
                                            uploads: undefined,
                                        })
                                    }
                                />
                                <CastingDetails
                                    profile={form}
                                    themeStyle={themeStyle}
                                    isEditMode={isEditMode}
                                    onEditClick={onEditCLick}
                                    previewMode={previewModeValue}
                                    editFocusScope="wp-perf-attr"
                                    technicalLayout
                                    singleCardSections
                                    performerSidebarCompact
                                    allowedSections={['attributes']}
                                    onSubmitForm={payload =>
                                        onChange &&
                                        onChange({
                                            body: payload,
                                            uploads: undefined,
                                        })
                                    }
                                />
                                <CastingDetails
                                    profile={form}
                                    themeStyle={themeStyle}
                                    isEditMode={isEditMode}
                                    onEditClick={onEditCLick}
                                    previewMode={previewModeValue}
                                    editFocusScope="wp-perf-links"
                                    technicalLayout
                                    allowedSections={['links']}
                                    onSubmitForm={payload =>
                                        onChange &&
                                        onChange({
                                            body: payload,
                                            uploads: undefined,
                                        })
                                    }
                                />
                                {shareLinkBlock}
                            </Stack>
                        </Grid>
                    </Grid>
                ) : isVoiceoverProfile ? (
                    isBelowLg ? (
                        <Stack spacing={4}>
                            <Stack spacing={2.5}>
                                {voiceoverLeftDetails}
                                {voiceoverRightDetails}
                            </Stack>
                            {demoReelBlock}
                            {audioReelBlock}
                            {shareLinkBlock}
                        </Stack>
                    ) : (
                        <Grid container spacing={4}>
                            <Grid item xs={12} lg={8}>
                                <Stack spacing={2.5}>
                                    {voiceoverLeftDetails}
                                    {demoReelBlock}
                                    {audioReelBlock}
                                </Stack>
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <Stack spacing={2.5}>
                                    {voiceoverRightDetails}
                                    {shareLinkBlock}
                                </Stack>
                            </Grid>
                        </Grid>
                    )
                ) : isBelowMd ? (
                    <Stack gap={3}>
                        {profilePictureBlock}
                        {imagesBlock}
                        {detailsStack}
                        {demoReelBlock}
                        {audioReelBlock}
                        {shareLinkBlock}
                    </Stack>
                ) : (
                    <Stack direction="row" gap={3}>
                        <Stack sx={{ flex: 0.3 }}>
                            {profilePictureBlock}
                            {imagesBlock}
                            {shareLinkBlock}
                            {demoReelBlock}
                            {audioReelBlock}
                        </Stack>
                        {detailsStack}
                    </Stack>
                )}
            </Container>
        );
});

const CastingProfile = memo(CastingProfileInner);
CastingProfile.displayName = 'CastingProfile';

export { CastingProfile };
